import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Award, Users, MessageCircle, TrendingUp, GraduationCap, Briefcase, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  'award': Award,
  'users': Users,
  'message-circle': MessageCircle,
  'trending-up': TrendingUp,
  'graduation-cap': GraduationCap,
  'briefcase': Briefcase
};

export const TrainingCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);
  const navigate = useNavigate();
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/public/training-categories`);
      setCategories(response.data || []);
    } catch (err) {
      console.error('Error fetching categories:', err);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  // Determine icon based on category title or use a default
  const getIcon = (category) => {
    const title = category.title?.toLowerCase() || '';
    if (title.includes('quality')) return Award;
    if (title.includes('leadership') || title.includes('management')) return Users;
    if (title.includes('communication')) return MessageCircle;
    if (title.includes('soft skill')) return TrendingUp;
    if (title.includes('faculty') || title.includes('development')) return GraduationCap;
    return Briefcase;
  };

  return (
    <section id="programs" className="py-20 bg-white" ref={ref}>
      <div className="container mx-auto px-4">
        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
          </div>
        )}

        {/* Categories Grid */}
        {!loading && categories.length > 0 && (
          <motion.div
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            {categories.map((category) => {
              const Icon = getIcon(category);

              const handleCardClick = () => {
                // Navigate to training calendar with category filter
                navigate(`/training-calendar?category=${category.id}`);
              };

              const handleButtonClick = (e) => {
                e.stopPropagation();
                navigate(`/training-calendar?category=${category.id}`);
              };

              return (
                <motion.div key={category.id} variants={cardVariants}>
                  <Card
                    className="group hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-blue-200 cursor-pointer h-full"
                    onClick={handleCardClick}
                  >
                    <CardHeader>
                      <motion.div
                        className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-600 transition-colors duration-300"
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        transition={{ duration: 0.3 }}
                      >
                        <Icon className="h-8 w-8 text-blue-600 group-hover:text-white transition-colors duration-300" />
                      </motion.div>
                      <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                        {category.title}
                      </CardTitle>
                      <CardDescription className="text-gray-600">
                        {category.description || 'Explore our comprehensive training programs in this category'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {/* Show courses if available */}
                      {category.courses && category.courses.length > 0 && (
                        <div className="space-y-2">
                          <p className="text-sm font-semibold text-gray-700 mb-2">Popular Courses:</p>
                          <ul className="space-y-1">
                            {category.courses.slice(0, 3).map((course, idx) => (
                              <motion.li
                                key={idx}
                                className="text-sm text-gray-600 flex items-center gap-2"
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.1 }}
                              >
                                <span className="w-1.5 h-1.5 bg-blue-600 rounded-full"></span>
                                {typeof course === 'string' ? course : course.name || course.title}
                              </motion.li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <Button
                        variant="ghost"
                        className="w-full mt-4 text-blue-600 hover:text-blue-700 hover:bg-blue-50 transition-colors"
                        onClick={handleButtonClick}
                      >
                        Explore Programs →
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        )}

        {/* Empty State */}
        {!loading && categories.length === 0 && (
          <div className="text-center py-10">
            <p className="text-gray-600 text-lg mb-4">
              No training categories available at the moment.
            </p>
            <Button onClick={() => navigate('/training-calendar')}>
              View Training Calendar
            </Button>
          </div>
        )}

        {/* CTA Section */}
        {!loading && categories.length > 0 && (
          <motion.div
            className="mt-16 text-center"
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            <Button
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg transition-all hover:scale-105 hover:shadow-xl"
              onClick={() => navigate('/training-calendar')}
            >
              View All Training Programs
            </Button>
          </motion.div>
        )}
      </div>
    </section>
  );
};
